import { Divider, Stack, Typography } from '@mui/material';
import {
  type HighFrequencyBackupSchedules,
  type DlsgPolicyConfig,
  ConditionOperatorEnum,
  type DlsgPolicy,
  type ResourceIdentifiers,
} from '@repo/api-gw-sdk';

import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';
import { PanelSize } from '@/contexts/workspaceContext';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';

import { Accordion, AccordionDetails, AccordionSummary } from '../accordion';
import { OptionMenu } from '../option-menu/option-menu';
import { Panels } from '../panels/panels';
import { resourceId } from '../queryBuilder/properties';
import { ResourceItem } from '../resourcesSelection/resourceItem';
import { Icon } from '../shared/icon';
import { Tag } from '../tag';
import { VaultTag } from '../vaults/vaultTag';

export const StorageGatewayPolicy = ({
  policy,
  onUpdate,
}: {
  policy: DlsgPolicy;
  onUpdate?: () => void;
}) => {
  const dal = useDAL();
  const { rightPanel } = useWorkspace();
  const vaultId = policy.policyPlan?.backupSchedule?.vaultId;
  const { vaults } = useBackupVaults();
  const { data: resources } = dal.inventory.useListResourceIdentifiers({
    property: resourceId.name,
    operator: ConditionOperatorEnum.In,
    value: policy.resourceInclusion,
  });

  const vault = vaults.find((v) => v.id === vaultId);
  if (!vault) {
    return null;
  }

  return (
    <Stack
      gap='24px'
      sx={{
        paddingY: '24px',
        border: '1px solid var(--mui-palette-divider)',
        borderRadius: '16px',
        backgroundColor: 'var(--mui-palette-background-paper)',
      }}
    >
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        margin={'0 24px 0 40px'}
      >
        <Stack direction='row' alignItems='center' gap='16px'>
          <Typography variant='h3'>{policy.name}</Typography>
        </Stack>
        {onUpdate && (
          <OptionMenu
            options={['Edit']}
            onOptionSelected={(option) => {
              if (option === 'Edit') {
                rightPanel.setComponent({
                  panel: Panels.StorageGatewayWizard,
                  props: {
                    onSave: async () => {
                      onUpdate();
                    },
                    onClose: () => rightPanel.setIsOpen(false),
                    entity: policy,
                  },
                });
                rightPanel.setIsOpen(true, PanelSize.Full);
              }
            }}
          />
        )}
      </Stack>
      <Stack gap='12px' divider={<Divider />}>
        <ResourceSelection
          resources={resources?.resources || []}
          isClickable={!!onUpdate}
        />
        <PolicyConfig policyConfigs={policy.policyPlan?.dlsgConfig || []} />
        <StorageAndRetention schedule={policy.policyPlan?.backupSchedule} />
      </Stack>
    </Stack>
  );
};
const ResourceSelection = ({
  resources,
  isClickable,
}: {
  resources: ResourceIdentifiers[];
  isClickable: boolean;
}) => {
  return (
    <Accordion className='mx-[40px]'>
      <AccordionSummary>
        <Typography>Resource selection</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap='24px'>
          <Stack gap='12px'>
            {resources.map((resource) => (
              <ResourceItem
                key={resource.id}
                resource={resource}
                isClickable={isClickable}
              />
            ))}
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const StorageAndRetention = ({
  schedule,
}: {
  schedule?: HighFrequencyBackupSchedules;
}) => {
  if (!schedule) {
    return null;
  }

  return (
    <Accordion className='mx-[40px]'>
      <AccordionSummary>
        <Stack
          width='100%'
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography>Storage & retention</Typography>
          <Stack className='more-info'>
            <VaultTag vault={schedule?.vaultId} />
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap='12px' divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
          <RowWrapper label='Storage location'>
            <VaultTag vault={schedule?.vaultId} />
          </RowWrapper>
          <RowWrapper label='Retention'>
            <Typography>{schedule.backupRetentionDays} days</Typography>
          </RowWrapper>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const PolicyConfig = ({
  policyConfigs,
}: {
  policyConfigs: DlsgPolicyConfig[];
}) => {
  return (
    <Accordion className='mx-[40px]'>
      <AccordionSummary>
        <Typography>Objects to migrate</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack gap='40px' divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
          {policyConfigs.map((config, idx) => (
            <DlsgPolicyConfigItem key={idx} config={config} />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const DlsgPolicyConfigItem = ({ config }: { config: DlsgPolicyConfig }) => {
  const isAllTime = config.migrationDelayHours === 0;
  const objectAgeTxt = isAllTime
    ? 'from all time'
    : `older than ${config.migrationDelayHours / 24} days`;
  const prefixesTxt = config.pathPrefixes.length
    ? ' in selected prefixes:'
    : '';

  return (
    <Stack gap='24px'>
      <Typography variant='subtitle1' component='span'>
        Migrate objects{' '}
        <Typography variant='subtitle1' component='span' fontWeight='medium'>
          {objectAgeTxt}
        </Typography>
        {prefixesTxt}
      </Typography>
      {!!config.pathPrefixes.length && (
        <Stack direction='row' gap='8px' flexWrap='wrap'>
          {config.pathPrefixes.map((prefix) => (
            <Tag key={prefix} content={prefix} variant='secondary' />
          ))}
        </Stack>
      )}
      {config.isTruncateData && (
        <Stack gap='8px' direction='row' alignItems='center'>
          <Icon iconClass='material-symbols-check' />
          <Typography variant='subtitle1' fontWeight='medium'>
            Objects will be deleted in source after copy.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

const RowWrapper = ({
  label,
  children,
}: React.PropsWithChildren<{
  label: string;
}>) => {
  return (
    <Stack gap='12px' direction='row' alignItems='center'>
      <Typography width='180px'>{label}</Typography>
      {children}
    </Stack>
  );
};
